import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { Typography } from 'packages/common-frontend/components/Typography';
import {
  CreditFacility,
  useCreditFacilitiesOrThrow,
} from '~/contexts/CreditFacilitiesProvider';
import { GetCreditFacilitiesByOrganizationDocumentGQLType } from '~/contexts/CreditFacilitiesProvider/requests/getCreditFacilities.gql.generated';
import {
  SelectFormField,
  TextInputFormField,
} from '~/packages/common-frontend/components/FormField';
import { useUpdateCreditFacilityNameMutation } from '~/packages/credit-facility/requests/requests.gql.generated';

// Constants
export const FORM_ID = 'UpdateFacilityNameForm';

// Types
const FormValuesStruct = z.object({
  facilityId: z.string().trim().uuid(),
  facilityName: z
    .string({ message: 'Must provide a valid name' })
    .trim()
    .min(5, { message: 'Must be at least 5 characters long' }),
});
type FormValues = z.infer<typeof FormValuesStruct>;

// Fragments
type UpdateFacilityNameFormProps = {
  externalErrors: (Error | undefined)[];
  facilities: CreditFacility[];
  onSubmit: (data: FormValues) => void;
};
const UpdateFacilityNameForm = ({
  externalErrors,
  facilities,
  onSubmit,
}: UpdateFacilityNameFormProps) => {
  // Facility selector
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>();
  const selectedFacility = useMemo(
    () =>
      facilities.find((facility) => facility.id === selectedFacilityId) ||
      // Pre-select first facility
      (facilities[0] as CreditFacility | undefined), // TS index array lookup can be undefined
    [facilities, selectedFacilityId],
  );

  const initialValues = useMemo(
    () => ({
      facilityId: selectedFacility?.id ?? '',
      facilityName: selectedFacility?.name ?? '',
    }),
    [selectedFacility],
  );

  const options = useMemo(
    () =>
      facilities.map((facility) => ({
        label: facility.name,
        value: facility.id,
      })),
    [facilities],
  );

  return (
    <Formik
      // This is important: Setting `enableReinitialize=true` tells Formik to reset the form to
      // `initialValues` whenever `initialValues` changes, and it changes every time
      // `selectedFacility` changes. So each time a different facility is selected, we reset the
      // form with updated `initialValues`. This has the effect of pre-filling the "new facility
      // name" field with the currently-selected facility name.
      enableReinitialize
      validationSchema={toFormikValidationSchema(FormValuesStruct)}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {() => (
        <>
          <Flex direction="column">
            {externalErrors.map((error) => {
              if (!error) return null;
              return (
                <Alert key={error.message} status="error">
                  <AlertIcon />{' '}
                  {error.message.replace('Validation error: ', '')}
                </Alert>
              );
            })}
          </Flex>
          <VStack as={Form} id={FORM_ID} spacing="10px">
            <SelectFormField
              label="Facility"
              name="facilityId"
              options={options}
              onChange={setSelectedFacilityId}
            />
            <TextInputFormField
              label="New Facility Name"
              name="facilityName"
              type="text"
              placeholder="Enter facility name"
            />
          </VStack>
        </>
      )}
    </Formik>
  );
};

// Exports
type Props = { modalControls: ReturnType<typeof useDisclosure> };
export const UpdateFacilityNameModal = ({ modalControls }: Props) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const [
    updateCreditFacilityName,
    updateCreditFacilityNameMutation,
  ] = useUpdateCreditFacilityNameMutation({
    awaitRefetchQueries: true,
    refetchQueries: [GetCreditFacilitiesByOrganizationDocumentGQLType],
  });

  return (
    <Modal isOpen={modalControls.isOpen} onClose={modalControls.onClose}>
      <ModalOverlay />
      <ModalContent padding="1rem" minWidth={['100%', '90%', '600px']}>
        <ModalHeader>
          <Typography variant="section-title" trim>
            Update Facility Name
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody minHeight="12rem">
          <Flex direction="column" gap="1rem">
            <UpdateFacilityNameForm
              facilities={facilities}
              externalErrors={[updateCreditFacilityNameMutation.error]}
              onSubmit={async (formData) => {
                try {
                  const facilityToUpdate = facilities.find(
                    (facility) => facility.id === formData.facilityId,
                  );

                  if (facilityToUpdate?.name !== formData.facilityName) {
                    await updateCreditFacilityName({
                      variables: {
                        creditFacilityId: formData.facilityId,
                        name: formData.facilityName,
                      },
                    });
                  }
                  modalControls.onClose();
                } catch (error) {
                  // Errors are handled through `updateCreditFacilityNameMutation.error`
                  // Do nothing...
                }
              }}
            />
          </Flex>
        </ModalBody>

        <ModalFooter gap="2rem" marginBottom="0.5rem">
          <Button
            variant="primary-muted"
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
          >
            Cancel
          </Button>
          <Button
            form={FORM_ID}
            type="submit"
            variant="primary"
            colorScheme="v2-aurora"
            isLoading={updateCreditFacilityNameMutation.loading}
            isDisabled={!facilities.length}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
